import React, { useCallback, useEffect, useRef, useState } from 'react';
import { fmtQty, importApi } from './psApi';
import { ItemPicker, Modal, useConfirm } from './PsModals';

const STEPS = ['Choose file', 'Columns', 'Check file', 'Product', 'Review', 'Create'];
const ACT = { link: ['Link', 'link'], create: ['Create in Orcanos', 'create'], pick: ['Pick', 'pick'], skip: ['Skip', 'skip'] };
const KIND = { added: ['Added', 'added'], qty: ['Qty', 'qty'], edit: ['Find / ref. des.', 'rev'], removed: ['Not in file', 'removed'], same: ['Same', 'same'] };
const STATUS = { draft: ['Draft', ''], creating: ['Running', 'warn'], done: ['Done', 'ok'], failed: ['Not finished', 'bad'], interrupted: ['Interrupted', 'bad'] };

function stepOf(st) {
  if (!st) return 1;
  if (['creating', 'done', 'failed', 'interrupted'].includes(st.status) || st.job) return 6;
  if (st.target?.mode && st.check?.ok) return 5;
  if (st.check) return 3;
  return 1;
}

/**
 * Import BOM file (2026-10-29) — design/DESIGN_BOM_IMPORT.md, mockup artboard "BOM file import" on
 * https://claude.ai/artifact/B1dQz7X7FdbfirGmkU7jjY. Parts are matched in Orcanos and missing ones
 * created there; the structure is written here (or into a proposal branch). Nothing is written
 * before Create — the server enforces every rule; the buttons are hints.
 */
export default function PsImport({ roots, importId, setImportId, go, showToast, onAuth, onDone }) {
  if (!importId) return <ImportList setImportId={setImportId} go={go} showToast={showToast} onAuth={onAuth} />;
  return <Wizard key={importId} id={importId === 'new' ? '' : importId} roots={roots} setImportId={setImportId}
    go={go} showToast={showToast} onAuth={onAuth} onDone={onDone} />;
}

function ImportList({ setImportId, go, showToast, onAuth }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  const [confirmEl, ask] = useConfirm();
  const load = useCallback(async () => {
    try { setRows((await importApi.list()).imports); } catch (e) { if (!onAuth(e)) setError(e.message); }
  }, [onAuth]);
  useEffect(() => { load(); }, [load]);
  return (
    <>
      <div className="ps-toolbar">
        <p className="ps-muted">Read an Excel or CSV BOM. Parts are matched in Orcanos; missing parts are created in Orcanos; the structure is kept here.</p>
        <button type="button" className="ps-btn" onClick={() => setImportId('new')}>+ Import BOM file</button>
      </div>
      {error && <div className="ps-error" role="alert">{error}</div>}
      <h3>Recent imports</h3>
      {rows && !rows.length && <div className="ps-empty"><p>No imports yet.</p></div>}
      {rows && rows.length > 0 && (
        <div className="ps-tw">
          <div className="ps-thead ps-g-imp"><span>File</span><span>Product</span><span>Result</span><span>When</span><span /></div>
          {rows.map((r) => {
            const [label, tone] = STATUS[r.status] || [r.status, ''];
            return (
              <div key={r.id} className="ps-trow ps-g-imp">
                <span>{r.file_name}</span>
                <span>{r.root_key ? <span className="ps-ikey">{r.root_key}</span> : <span className="ps-muted">—</span>}</span>
                <span className={'ps-st' + (tone ? ` ps-st--${tone}` : '')}>{label}{r.branch_id ? ' · branch' : ''}{r.created ? ` · ${r.created} new part${r.created === 1 ? '' : 's'}` : ''}</span>
                <span className="ps-muted ps-small">{(r.created_at || '').slice(0, 16)} UTC</span>
                <span className="ps-acts">
                  {r.status === 'done' && r.root && (
                    <button type="button" className="ps-btn ps-btn--link"
                      onClick={() => go('ps-structures', { root: r.root, branch: r.branch_id || '' })}>{r.branch_id ? 'Open branch' : 'Open product'}</button>
                  )}
                  <button type="button" className="ps-btn ps-btn--link" onClick={() => setImportId(r.id)}>{r.status === 'done' ? 'View' : 'Continue'}</button>
                  <button type="button" className="ps-btn ps-btn--link" disabled={r.status === 'creating'}
                    onClick={async () => {
                      if (!(await ask({ title: 'Delete import?', message: `Delete the import record of ${r.file_name}? Parts it created stay in Orcanos and lines it wrote stay in the structure.` }))) return;
                      try { await importApi.remove(r.id); showToast('Import deleted.'); load(); } catch (e) { if (!onAuth(e)) showToast(e.message, 'error'); }
                    }}>Delete</button>
                </span>
              </div>
            );
          })}
        </div>
      )}
      {confirmEl}
    </>
  );
}

function Wizard({ id, roots, setImportId, go, showToast, onAuth, onDone }) {
  const [st, setSt] = useState(null);
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const fail = useCallback((e) => { if (!onAuth(e)) setError(e.message); }, [onAuth]);

  useEffect(() => {
    if (!id) return;
    importApi.get(id).then((s) => { setSt(s); setStep(stepOf(s)); }).catch(fail);
  }, [id, fail]);

  const run = async (label, fn) => {
    setBusy(label);
    setError('');
    try { return await fn(); } catch (e) { fail(e); return null; } finally { setBusy(''); }
  };
  const locked = !!(st && (st.status === 'done' || st.job?.running || Object.keys(st.result?.created || {}).length));

  return (
    <>
      <div className="ps-toolbar">
        <p className="ps-muted">Nothing is written to Orcanos or to the structure before <b>Create</b>.</p>
        <button type="button" className="ps-btn ps-btn--ghost" onClick={() => setImportId('')}>Recent imports</button>
      </div>
      <nav className="ps-steps" aria-label="Import steps">
        {STEPS.map((label, i) => {
          const n = i + 1;
          const can = st && n < step && !locked;
          return (
            <button key={label} type="button" disabled={!can && n !== step}
              className={'ps-step' + (n === step ? ' ps-step--cur' : n < step ? ' ps-step--done' : '')}
              onClick={() => { if (can) setStep(n); }}><b>{n}</b>{label}</button>
          );
        })}
      </nav>
      {error && <div className="ps-error" role="alert">{error}</div>}
      {step === 1 && <StepFile st={st} busy={busy} run={run} setSt={setSt} setImportId={setImportId} next={() => setStep(2)} />}
      {step === 2 && st && <StepColumns st={st} busy={busy} run={run} setSt={setSt} back={() => setStep(1)} next={() => setStep(3)} onAuth={onAuth} />}
      {step === 3 && st && <StepCheck st={st} back={() => setStep(2)} next={() => setStep(4)} />}
      {step === 4 && st && <StepProduct st={st} roots={roots} busy={busy} run={run} setSt={setSt} back={() => setStep(3)} next={() => setStep(5)} />}
      {step === 5 && st && <StepReview st={st} busy={busy} run={run} setSt={setSt} back={() => setStep(4)} next={() => setStep(6)} onAuth={onAuth} />}
      {step === 6 && st && <StepCreate st={st} setSt={setSt} run={run} busy={busy} go={go} setImportId={setImportId} showToast={showToast} onAuth={onAuth} onDone={onDone} />}
    </>
  );
}

function StepFile({ st, busy, run, setSt, setImportId, next }) {
  const fileRef = useRef(null);
  const imgRef = useRef(null);
  const [confirmEl, ask] = useConfirm();
  const pickFile = async (f) => {
    if (!f) return;
    const s = await run('Reading the file…', () => importApi.upload(f));
    if (s) { setSt(s); setImportId(s.id); }
  };
  const addImages = async (files) => {
    if (!files.length || !st) return;
    const s = await run('Adding images…', () => importApi.addImages(st.id, files));
    if (s) setSt(s);
  };
  const changeSheet = async (sheet) => {
    const s = await run('Reading the sheet…', () => importApi.mapping(st.id, { sheet, mapping: {}, sheet_changed: true }));
    if (s) setSt(s);
  };
  return (
    <>
      {!st && (
        <label className="ps-drop" onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); pickFile(e.dataTransfer.files[0]); }}>
          <h2>Drop a BOM file here</h2>
          <span className="ps-muted">.xlsx or .csv, up to 20 MB. One sheet per import.</span>
          <span className="ps-muted ps-small">Either a <b>Level</b> column (indented BOM: 0, 1, 2…) or a <b>Parent</b> column (each row names its parent’s part number).</span>
          <span className="ps-btn ps-btn--ghost" role="button" tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileRef.current?.click(); }}>{busy || 'Choose file…'}</span>
          <input ref={fileRef} type="file" accept=".xlsx,.csv" hidden onChange={(e) => pickFile(e.target.files[0])} />
        </label>
      )}
      {st && (
        <>
          <div className="ps-filecard">
            <div style={{ flex: '1 1 260px' }}><b>{st.file_name}</b>
              <p className="ps-muted ps-small">{st.layout.sheets?.length} sheet{st.layout.sheets?.length === 1 ? '' : 's'} · {st.layout.rows} rows on “{st.layout.sheet}”</p></div>
            {st.layout.sheets?.length > 1 && (
              <label className="ps-field" style={{ flex: '0 1 240px' }}>Sheet
                <select value={st.layout.sheet} onChange={(e) => changeSheet(e.target.value)} disabled={!!busy}>
                  {st.layout.sheets.map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </label>
            )}
          </div>
          <div className="ps-filecard" style={{ marginTop: 10 }}>
            <div style={{ flex: '1 1 260px' }}><b>Images (optional)</b>
              <p className="ps-muted ps-small">For an <b>Image</b> column with file names: add the pictures or one .zip. Pictures placed inside the Excel sheet and https:// links in the column need nothing here. Images go on <b>new</b> parts only.</p></div>
            <span className="ps-small">{st.images.length ? `${st.images.length} image${st.images.length === 1 ? '' : 's'} added` : 'None added'}</span>
            <button type="button" className="ps-btn ps-btn--sm ps-btn--ghost" disabled={!!busy} onClick={() => imgRef.current?.click()}>Add images…</button>
            {st.images.length > 0 && (
              <button type="button" className="ps-btn ps-btn--sm ps-btn--link" disabled={!!busy}
                onClick={async () => {
                  if (!(await ask({ title: 'Remove all images?', message: `Remove the ${st.images.length} image${st.images.length === 1 ? '' : 's'} added to this import?`, confirmLabel: 'Remove all' }))) return;
                  const s = await run('…', () => importApi.clearImages(st.id)); if (s) setSt(s);
                }}>Remove all</button>
            )}
            <input ref={imgRef} type="file" multiple accept=".png,.jpg,.jpeg,.gif,.webp,.bmp,.zip" hidden
              onChange={(e) => { addImages(Array.from(e.target.files)); e.target.value = ''; }} />
          </div>
          {busy && <p className="ps-muted ps-small" style={{ marginTop: 8 }}>{busy}</p>}
          <div className="ps-foot"><span /><button type="button" className="ps-btn" disabled={!!busy} onClick={next}>Columns →</button></div>
        </>
      )}
      {confirmEl}
    </>
  );
}

function StepColumns({ st, busy, run, setSt, back, next, onAuth }) {
  const [mapping, setMapping] = useState(st.mapping || {});
  const [headerRow, setHeaderRow] = useState((st.layout.header_row || 0) + 1);
  const [fields, setFields] = useState(null);
  const [fieldsErr, setFieldsErr] = useState('');
  useEffect(() => {
    importApi.formFields().then((r) => setFields(r.fields)).catch((e) => { if (!onAuth(e)) setFieldsErr(e.message); });
  }, [onAuth]);
  const roles = Object.entries(st.roles || {});
  const used = new Set(Object.values(mapping));
  const hasPn = used.has('part_number');
  const hasTree = used.has('level') || used.has('parent');
  const set = (i, v) => setMapping((m) => { const n = { ...m }; if (v) n[String(i)] = v; else delete n[String(i)]; return n; });
  const reread = async () => {
    const s = await run('Reading the sheet…', () => importApi.mapping(st.id, { sheet: st.layout.sheet, header_row: Math.max(0, Number(headerRow) - 1), mapping: {}, sheet_changed: true }));
    if (s) { setSt(s); setMapping(s.mapping || {}); }
  };
  const check = async () => {
    const s = await run('Reading every row…', () => importApi.mapping(st.id, { sheet: st.layout.sheet, header_row: st.layout.header_row, mapping }));
    if (s) { setSt(s); next(); }
  };
  return (
    <>
      <div className="ps-banner ps-banner--note">
        {st.mapping_saved ? 'Columns as you set them last time for a file with these headers.' : 'Suggested from the header names.'}
        {' '}<b>Part number</b> and <b>Level</b> (or <b>Parent</b>) are required. Columns going to an Orcanos field are used <b>only for parts that will be created</b>; existing parts are never changed.
      </div>
      <div className="ps-row" style={{ marginBottom: 10 }}>
        <label className="ps-lbl" htmlFor="ps-hdr">Header row</label>
        <input id="ps-hdr" className="ps-in ps-in--qty" type="number" min="1" value={headerRow} onChange={(e) => setHeaderRow(e.target.value)} />
        <button type="button" className="ps-btn ps-btn--sm ps-btn--ghost" disabled={!!busy || Number(headerRow) - 1 === st.layout.header_row} onClick={reread}>Use this row</button>
      </div>
      <div className="ps-tw">
        <div className="ps-thead ps-g-imap"><span>File column</span><span>First values</span><span>Use as</span></div>
        {(st.layout.headers || []).map((h, i) => (
          <div key={i} className="ps-trow ps-g-imap">
            <b>{h || <span className="ps-muted">(no header)</span>}</b>
            <span className="ps-muted ps-small">{st.layout.samples?.[i] || ''}</span>
            <select className="ps-in" aria-label={`Use column ${h} as`} value={mapping[String(i)] || ''} onChange={(e) => set(i, e.target.value)}>
              <option value="">Not needed</option>
              {roles.map(([k, label]) => <option key={k} value={k} disabled={used.has(k) && mapping[String(i)] !== k}>{label}</option>)}
              {(fields || []).length > 0 && (
                <optgroup label="Orcanos field (new parts only)">
                  {fields.map((f) => <option key={f.title} value={`field:${f.title}`}>{f.title}{f.mandatory ? ' *' : ''}</option>)}
                </optgroup>
              )}
              {mapping[String(i)]?.startsWith('field:') && !(fields || []).some((f) => `field:${f.title}` === mapping[String(i)]) && (
                <option value={mapping[String(i)]}>{mapping[String(i)].slice(6)}</option>
              )}
            </select>
          </div>
        ))}
      </div>
      {fieldsErr && <p className="ps-muted ps-small" style={{ marginTop: 8 }}>Orcanos fields could not be read ({fieldsErr}) — new parts get a name and description only.</p>}
      {!fields && !fieldsErr && <p className="ps-muted ps-small" style={{ marginTop: 8 }}>Reading the Part form from Orcanos…</p>}
      <div className="ps-foot">
        <button type="button" className="ps-btn ps-btn--ghost" onClick={back}>← Back</button>
        <span className="ps-row">
          {(!hasPn || !hasTree) && <span className="ps-muted ps-small">Choose {!hasPn ? 'the Part number' : ''}{!hasPn && !hasTree ? ' and ' : ''}{!hasTree ? 'a Level or Parent column' : ''}.</span>}
          <button type="button" className="ps-btn" disabled={!!busy || !hasPn || !hasTree} onClick={check}>{busy || 'Check file →'}</button>
        </span>
      </div>
    </>
  );
}

function sevTone(sev) { return sev === 'error' ? 'bad' : sev === 'warning' ? 'warn' : ''; }

function StepCheck({ st, back, next }) {
  const [filter, setFilter] = useState('all');
  const c = st.check;
  const rows = filter === 'all' ? c.rows : c.rows.filter((r) => r.issues.length);
  return (
    <>
      <div className={'ps-banner ' + (c.ok ? 'ps-banner--note' : 'ps-banner--loc')}>
        <b>{c.ok ? 'Ready to continue' : `${c.errors} error${c.errors === 1 ? '' : 's'} — fix the file and import it again`}</b>
        <span>{c.parts} parts · {c.links} lines · {c.depth + 1} levels · {c.errors} errors · {c.warnings} warnings{st.sheet_images ? ` · ${st.sheet_images} pictures in the sheet` : ''}</span>
      </div>
      {c.general.map((i, k) => <div key={k} className={`ps-banner ${i.sev === 'error' ? 'ps-banner--loc' : 'ps-banner--warn'}`}>{i.text}</div>)}
      <div className="ps-toolbar">
        <div className="ps-pills">
          <button type="button" className={'ps-pill' + (filter === 'all' ? ' ps-pill--on' : '')} onClick={() => setFilter('all')}>All rows</button>
          <button type="button" className={'ps-pill' + (filter === 'iss' ? ' ps-pill--on' : '')} onClick={() => setFilter('iss')}>Errors and warnings</button>
        </div>
        <span className="ps-muted ps-small">{rows.length} rows · products: {c.tops.join(', ') || '—'}</span>
      </div>
      <div className="ps-tw">
        <div className="ps-thead ps-g-ichk"><span>Row</span><span>Part number · description</span><span className="ps-num">Qty</span><span>Unit</span><span>Find</span><span>Check</span></div>
        {rows.map((r, k) => (
          <div key={k} className={'ps-trow ps-g-ichk' + (r.issues.some((i) => i.sev === 'error') ? ' ps-trow--loc' : '')}>
            <span className="ps-muted ps-small">{r.row || ''}</span>
            <span className="ps-cell-item"><span style={{ width: r.depth * 22, flex: 'none' }} /><span className="ps-ikey">{r.pn || '—'}</span><span>{r.name}</span>
              {r.image && <span className="ps-badge ps-badge--doc">image</span>}</span>
            <span className="ps-num">{r.depth ? fmtQty(r.qty ?? 1) : ''}</span>
            <span>{r.unit}</span>
            <span>{r.find_no}</span>
            <span className="ps-small">{r.issues.length
              ? r.issues.map((i, j) => <div key={j} className={'ps-st' + (sevTone(i.sev) ? ` ps-st--${sevTone(i.sev)}` : '')}>{i.text}</div>)
              : <span className="ps-st ps-st--ok">OK</span>}</span>
          </div>
        ))}
      </div>
      <div className="ps-foot">
        <button type="button" className="ps-btn ps-btn--ghost" onClick={back}>← Back</button>
        <button type="button" className="ps-btn" disabled={!c.ok} onClick={next}>Product →</button>
      </div>
    </>
  );
}

function StepProduct({ st, roots, busy, run, setSt, back, next }) {
  const t = st.target || {};
  const [mode, setMode] = useState(t.mode || 'new');
  const [root, setRoot] = useState(t.root || roots[0]?.node || '');
  const [branchName, setBranchName] = useState(t.branch_name || `Import ${st.file_name} ${new Date().toISOString().slice(0, 10)}`);
  const tops = st.check?.tops || [];
  const save = async () => {
    const s = await run('Saving…', () => importApi.target(st.id, { mode, root: mode === 'existing' ? root : '', branch_name: branchName }));
    if (s) { setSt(s); next(); }
  };
  return (
    <>
      <div className="ps-setcard">
        <h2>{tops.length === 1 ? <>The file’s top row is <span className="ps-ikey">{tops[0]}</span></> : <>The file has {tops.length} top rows: {tops.join(', ')}</>}</h2>
        <button type="button" className="ps-opt" onClick={() => setMode('new')}>
          <span className={'ps-dot' + (mode === 'new' ? ' ps-dot--on' : '')} aria-hidden="true" />
          <span><b>New product{tops.length > 1 ? 's' : ''}</b><br /><span className="ps-muted ps-small">Each top row becomes a product in Product structures and the file is its structure. A sub-assembly that other products already use, and that differs in the file, goes into a branch.</span></span>
        </button>
        <button type="button" className="ps-opt" onClick={() => setMode('existing')} disabled={tops.length !== 1 || !roots.length}>
          <span className={'ps-dot' + (mode === 'existing' ? ' ps-dot--on' : '')} aria-hidden="true" />
          <span><b>An existing product</b><br /><span className="ps-muted ps-small">The file goes into a <b>proposal branch</b>; you compare it with main and merge it on the Branches page. Main is never overwritten by a file.</span></span>
        </button>
        {mode === 'existing' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 8 }}>
            <label className="ps-field">Product
              <select value={root} onChange={(e) => setRoot(e.target.value)}>
                {roots.map((r) => <option key={r.node} value={r.node}>{r.key} · {r.name || '(not in the filter)'}</option>)}
              </select>
            </label>
          </div>
        )}
        <label className="ps-field" style={{ marginTop: 10 }}>Branch name (used when there are changes to review)
          <input value={branchName} maxLength={120} onChange={(e) => setBranchName(e.target.value)} />
        </label>
      </div>
      <div className="ps-foot">
        <button type="button" className="ps-btn ps-btn--ghost" onClick={back}>← Back</button>
        <button type="button" className="ps-btn" disabled={!!busy || (mode === 'existing' && !root)} onClick={save}>{busy || 'Review →'}</button>
      </div>
    </>
  );
}

function StepReview({ st, busy, run, setSt, back, next, onAuth }) {
  const [rv, setRv] = useState(null);
  const [err, setErr] = useState('');
  const [pickFor, setPickFor] = useState(null);
  const [allLinks, setAllLinks] = useState(false);
  const load = useCallback(async () => {
    setErr('');
    try { setRv(await importApi.review(st.id)); } catch (e) { if (!onAuth(e)) setErr(e.message); }
  }, [st.id, onAuth]);
  useEffect(() => { load(); }, [load]);
  const setPick = async (key, v) => {
    const r = await run('Saving…', () => importApi.picks(st.id, { [key]: v }));
    if (r) { setSt((s) => ({ ...s, picks: r.picks })); setPickFor(null); load(); }
  };
  const create = async () => {
    const s = await run('Starting…', () => importApi.create(st.id));
    if (s) { setSt(s); next(); }
  };
  if (err) return <><div className="ps-error" role="alert">{err}</div><div className="ps-foot"><button type="button" className="ps-btn ps-btn--ghost" onClick={back}>← Back</button></div></>;
  if (!rv) return <p className="ps-muted">Matching the file’s parts against the Part Catalog in Orcanos…</p>;
  const n = rv.counts;
  const links = allLinks ? rv.links : rv.links.slice(0, 150);
  return (
    <>
      <div className="ps-sum">
        <span><b>{n.link}</b> found in Orcanos — link</span>
        <span><b>{n.create}</b> new — create in Orcanos</span>
        <span><b>{n.pick}</b> to pick</span>
        <span><b>{n.skip}</b> skipped</span>
        <span><b>{rv.main}</b> lines into the structure</span>
        {rv.branch > 0 && <span><b>{rv.branch}</b> changes into a branch</span>}
        {rv.images > 0 && <span><b>{rv.images}</b> images · new parts only</span>}
      </div>
      {rv.notes.map((t, i) => <div key={i} className="ps-banner ps-banner--warn">{t}</div>)}
      {rv.shared.length > 0 && st.target?.mode === 'new' && (
        <div className="ps-banner ps-banner--br"><b>{rv.shared.join(', ')} {rv.shared.length === 1 ? 'is' : 'are'} shared</b>
          <span>Other products already use {rv.shared.length === 1 ? 'it' : 'them'} with other parts or quantities, so those changes go into a proposal branch — merge them later, everywhere or only in this product.</span></div>
      )}
      {st.target?.mode === 'existing' && rv.branch > 0 && (
        <div className="ps-banner ps-banner--br">Every difference from the product goes into the proposal branch <b>{st.target.branch_name}</b>. Main stays as it is until you merge.</div>
      )}
      {rv.already_products.length > 0 && st.target?.mode === 'new' && (
        <div className="ps-banner ps-banner--warn">{rv.already_products.join(', ')} {rv.already_products.length === 1 ? 'is' : 'are'} already a product — differences go into a branch.</div>
      )}
      {rv.missing_required.length > 0 && (
        <div className="ps-banner ps-banner--warn">The Part form marks {rv.missing_required.join(', ')} as required and no column fills {rv.missing_required.length === 1 ? 'it' : 'them'} — Orcanos may refuse the new parts. Map a column on Columns.</div>
      )}
      <h3>Parts</h3>
      <div className="ps-tw">
        <div className="ps-thead ps-g-irev"><span>Part number</span><span>In the file</span><span>Action</span><span>In Orcanos</span><span /></div>
        {rv.parts.map((p) => {
          const [label, cls] = ACT[p.action];
          return (
            <div key={p.key} className="ps-trow ps-g-irev">
              <span className="ps-ikey">{p.pn}</span>
              <span>{p.name || <span className="ps-muted">—</span>}{p.image && p.action === 'create' && <span className="ps-badge ps-badge--doc" style={{ marginLeft: 6 }}>image</span>}</span>
              <span className={`ps-act ps-act--${cls}`}>{label}</span>
              <span className="ps-small">{p.item ? <><span className="ps-ikey">{p.item.key}</span> {p.item.name}</> : null}
                {p.why && <span className="ps-muted">{p.item ? ' · ' : ''}{p.why}</span>}</span>
              <span className="ps-acts">
                {(p.candidates.length > 0 || p.action === 'pick') && <button type="button" className="ps-btn ps-btn--sm ps-btn--ghost" onClick={() => setPickFor(p)}>Pick…</button>}
                {p.action !== 'skip' && <button type="button" className="ps-btn ps-btn--link" onClick={() => setPick(p.key, 'skip')}>Skip</button>}
                {st.picks?.[p.key] && <button type="button" className="ps-btn ps-btn--link" onClick={() => setPick(p.key, '')}>Undo</button>}
              </span>
            </div>
          );
        })}
      </div>
      <h3>Lines{st.target?.mode === 'existing' ? ' (compared with the product)' : ''}</h3>
      <div className="ps-tw">
        <div className="ps-thead ps-g-ilnk"><span>Change</span><span>Goes to</span><span>Parent → child</span><span className="ps-num">Qty</span><span>Find</span></div>
        {links.map((l, i) => {
          const [label, cls] = KIND[l.kind] || [l.kind, 'same'];
          return (
            <div key={i} className="ps-trow ps-g-ilnk">
              <span className={`ps-chg ps-chg--${cls}`}>{label}</span>
              <span className="ps-small">{l.where === 'branch' ? 'Branch' : l.where === 'main' ? 'Structure' : '—'}</span>
              <span><span className="ps-ikey">{l.parent_pn}</span> → <span className="ps-ikey">{l.child_pn}</span></span>
              <span className="ps-num">{l.from_qty != null ? `${fmtQty(l.from_qty)} → ` : ''}{fmtQty(l.qty)}</span>
              <span>{l.find_no}</span>
            </div>
          );
        })}
      </div>
      {rv.links.length > 150 && !allLinks && <button type="button" className="ps-btn ps-btn--link" onClick={() => setAllLinks(true)}>Show all {rv.links.length} lines</button>}
      {rv.dropped > 0 && <p className="ps-muted ps-small" style={{ marginTop: 6 }}>{rv.dropped} line{rv.dropped === 1 ? '' : 's'} left out — a part on it is skipped.</p>}
      <div className="ps-foot">
        <button type="button" className="ps-btn ps-btn--ghost" onClick={back}>← Back</button>
        <button type="button" className="ps-btn" disabled={!!busy || n.pick > 0} onClick={create}>
          {busy || (n.pick > 0 ? `Pick or skip ${n.pick} first` : 'Create →')}</button>
      </div>
      {pickFor && (
        <PickModal part={pickFor} onClose={() => setPickFor(null)} onPick={(v) => setPick(pickFor.key, v)} />
      )}
    </>
  );
}

function PickModal({ part, onClose, onPick }) {
  const [picked, setPicked] = useState(null);
  return (
    <Modal title={`Pick a match for ${part.pn}`} wide onClose={onClose}
      footer={<>
        <button type="button" className="ps-btn ps-btn--ghost" onClick={() => onPick('create')} disabled={!part.in_file}>Create a new part instead</button>
        <button type="button" className="ps-btn ps-btn--ghost" onClick={onClose}>Cancel</button>
        <button type="button" className="ps-btn" disabled={!picked} onClick={() => onPick(picked.node)}>Use this one</button>
      </>}>
      {part.why && <p className="ps-muted ps-small">{part.why}</p>}
      {part.candidates.length > 0 && (
        <>
          <h3 style={{ marginTop: 0 }}>Same number in Orcanos</h3>
          <div className="ps-candlist">
            {part.candidates.map((c) => (
              <button key={c.node} type="button" className={'ps-cand' + (picked?.node === c.node ? ' ps-cand--on' : '')}
                disabled={c.type === 'DMS' && part.has_children} onClick={() => setPicked(c)}>
                <span className="ps-ikey">{c.key}</span><span>{c.name}</span><span className="ps-cand-note">{c.type === 'DMS' ? 'Document · leaf' : c.status}</span>
              </button>
            ))}
          </div>
        </>
      )}
      <h3>Or search Orcanos</h3>
      <ItemPicker picked={picked} onPick={setPicked} allowDocs={!part.has_children} />
    </Modal>
  );
}

function StepCreate({ st, setSt, run, busy, go, setImportId, showToast, onAuth, onDone }) {
  const running = !!st.job?.running;
  const tick = useRef(null);
  useEffect(() => {
    if (!running) return undefined;
    tick.current = setInterval(async () => {
      try {
        const s = await importApi.get(st.id);
        setSt(s);
        if (!s.job?.running) onDone?.();
      } catch (e) { onAuth(e); }
    }, 1000);
    return () => clearInterval(tick.current);
  }, [running, st.id, setSt, onAuth, onDone]);
  const j = st.job || {};
  const r = st.result || {};
  const errors = (running ? j.errors : r.errors) || [];
  const done = st.status === 'done';
  const pct = j.total ? Math.round((100 * (j.done || 0)) / j.total) : (running ? 5 : 100);
  return (
    <div className="ps-setcard">
      {running && (
        <>
          <h2>{j.stage}{j.total ? ` — ${j.done} of ${j.total} new parts` : ''}</h2>
          <div className="ps-prog"><i style={{ width: `${pct}%` }} /></div>
          <p className="ps-muted ps-small">You can leave this page; the import keeps running.</p>
          <div className="ps-foot">
            <span />
            <button type="button" className="ps-btn ps-btn--danger" disabled={!!j.stop || j.stage?.startsWith('Stopping')}
              onClick={async () => {
                if (!window.confirm('Stop the import after the part in flight? No lines are written; Create again finishes it.')) return;
                const s = await run('Stopping…', () => importApi.stop(st.id));
                if (s) setSt(s);
              }}>{j.stage?.startsWith('Stopping') ? 'Stopping…' : 'Stop import'}</button>
          </div>
        </>
      )}
      {!running && (
        <>
          <h2>{done ? 'Done' : st.status === 'interrupted' ? 'Interrupted — the server restarted' : 'Not finished'}</h2>
          <div className="ps-log">
            <div><span className="ps-st ps-st--ok">✓</span><span>{r.new_parts || 0} new part{r.new_parts === 1 ? '' : 's'} created in Orcanos{Object.keys(r.created || {}).length > (r.new_parts || 0) ? ` (${Object.keys(r.created).length} by this import in all)` : ''}</span></div>
            {done && <div><span className="ps-st ps-st--ok">✓</span><span>{r.main || 0} line{r.main === 1 ? '' : 's'} written to the structure</span></div>}
            {done && r.branch > 0 && <div><span className="ps-st ps-st--warn">!</span><span>{r.branch} change{r.branch === 1 ? '' : 's'} in a proposal branch — review and merge it</span></div>}
            {r.images > 0 && <div><span className="ps-st ps-st--ok">✓</span><span>{r.images} image{r.images === 1 ? '' : 's'} attached to new parts</span></div>}
            {done && r.attached && <div><span className="ps-st ps-st--ok">✓</span><span>The file is attached to the product’s part</span></div>}
          </div>
          {errors.length > 0 && (
            <>
              <h3>{done ? 'Notes' : 'What stopped it'}</h3>
              <ul className="ps-errlist">{errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
            </>
          )}
          <div className="ps-card-actions">
            {done && r.root && <button type="button" className="ps-btn" onClick={() => go('ps-structures', { root: r.root })}>Open structure</button>}
            {done && r.branch_id && <button type="button" className="ps-btn ps-btn--ghost" onClick={() => go('ps-structures', { root: r.root, branch: r.branch_id })}>Open branch</button>}
            {done && r.root && <button type="button" className="ps-btn ps-btn--ghost" onClick={() => go('ps-baselines', { root: r.root })}>Take a baseline</button>}
            {!done && (
              <button type="button" className="ps-btn" disabled={!!busy} onClick={async () => {
                const s = await run('Starting…', () => importApi.create(st.id));
                if (s) { setSt(s); showToast('Continuing — parts already created are reused.'); }
              }}>Create again</button>
            )}
            <button type="button" className="ps-btn ps-btn--ghost" onClick={() => setImportId('')}>Recent imports</button>
          </div>
        </>
      )}
    </div>
  );
}
